/**
 * The canonical routes, and what each theme calls them.
 *
 * ONE source of truth for both navs. V1/V2 show a tab bar, V3 shows an icon
 * rail, but they point at the same URLs — so switching theme keeps you on the
 * page you were looking at, which the old string-state navigation could not do.
 *
 * NOTE the gap: V3 has no Custom view. In public/index.html that is invisible
 * because its nav simply omits it, but once /custom is a real URL it has to
 * render something in every theme. `v3Nav: false` keeps it out of the V3 rail
 * while leaving the route reachable — it renders the shared view inside the V3
 * shell, exactly as Settings already does.
 */
/*
 * VARIATION SELECTOR-15. Appended to any glyph that ALSO has an emoji form, to
 * force the monochrome text presentation.
 *
 * Without it the V3 rail renders those glyphs through the system colour-emoji
 * font, so they arrive pre-coloured and ignore the rail's cyan — which is why
 * the sidebar came out looking like a set of mismatched pictograms rather than
 * one icon set. Every other glyph below is a pure geometric/maths character
 * with no emoji form, so it needs nothing.
 *
 * Kept as a named constant rather than typed inline: U+FE0E is invisible, and an
 * invisible character in a string literal is unreadable and easy to lose.
 */
const TEXT_PRESENTATION = '\uFE0E';

/*
 * ORDER IS THE ORIGINAL'S. Custom sits fifth, between Tests and Payouts, where
 * public/index.html's tab bar put it — the port had moved it to seventh, which
 * is a small thing to get wrong and an irritating one to live with for anyone
 * who navigates this bar by position rather than by reading it.
 */
export const NAV_ITEMS = [
  { path: '/overview',   tab: 'Overview',     v3Label: 'CMD',      icon: '⬡', v3Nav: true },
  { path: '/campaigns',  tab: 'Campaigns',    v3Label: 'OPS',      icon: '◈', v3Nav: true },
  { path: '/analytics',  tab: 'Analytics',    v3Label: 'INTEL',    icon: '◉', v3Nav: true },
  { path: '/tests',      tab: 'Tests',        v3Label: 'MISS',     icon: '⊕', v3Nav: true, sepBefore: true },
  { path: '/custom',     tab: 'Custom',       v3Label: 'CUSTOM',   icon: '◧', v3Nav: false },
  { path: '/payouts',    tab: 'Payouts',      v3Label: 'LEDGER',   icon: '⊞', v3Nav: true },
  { path: '/affiliates', tab: 'Affiliates',    v3Label: 'TEAM',     icon: '⧉', v3Nav: true, adminOnly: true,
    title: 'Affiliate logins and this week’s results' },
  // Was 🗄 (U+1F5C4), a full colour emoji with no text form at all — replaced
  // with a geometric glyph rather than fought with a variation selector.
  { path: '/archive',    tab: 'Archive',      v3Label: 'ARCHIVE',  icon: '▤', v3Nav: true,
    title: 'Campaign Archive — searchable all-time performance history' },
  { path: '/activity',   tab: 'Activity Log', v3Label: 'LOG',      icon: '≡', v3Nav: true, sepBefore: true },
  /*
   * Notifications is reachable from the nav AND from the bell in both shells.
   * That is deliberate duplication rather than an oversight: the bell is where
   * people go when they have noticed a badge, and the nav is where they go when
   * they are looking for the history without having been prompted. Leaving it
   * out of the nav would make the searchable archive discoverable only when
   * there happened to be something unread.
   *
   * `v3Nav: false` keeps it out of the V3 rail, which already carries the bell
   * in its ticker and has no room for a ninth icon — same treatment Custom gets.
   * The route stays reachable in every theme either way.
   */
  { path: '/notifications', tab: 'Notifications', v3Label: 'ALERTS', icon: '◇', v3Nav: false,
    title: 'Notification centre — every alert, searchable' },
  { path: '/settings',   tab: 'Settings',     v3Label: 'SETTINGS', icon: `⚙${TEXT_PRESENTATION}`, v3Nav: true },
];

export const DEFAULT_ROUTE = '/overview';
